import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InjectedFormProps, initialize, SubmissionError, reduxForm } from "redux-form";
import { PAGES } from "../../constants/localization";
import ActionLink from "../../components/Common/ActionLink";
import { ACTION_DISPLAY_TYPES, FIELD_CATEGORIES, MONEY_TRANSFER_STEPS, MONEY_TRANSFER_FIELD_IDS } from "../../constants/enums";
import { AddWorkflowStep, SetWorkflowPageNumber } from "../../actions/moneyTransfer";
import { MoneyTransferPage, WorkflowStep, MoneyTransferField } from "../../types/moneyTransfer";
import { RootState } from "../../types/redux";

import DynamicForm from "../../components/MoneyTransfer/DynamicForm";
import Action from "../../components/Common/Action";
import FooterLinks from "../../components/MoneyTransfer/FooterLinks";
import DisplaySection from "../../components/MoneyTransfer/DisplaySection";
import MoneyTransferApi from "../../api/moneyTransfer";
import { HTTP_STATUS_CODES } from "../../constants/api";
import { ValidateWorkflowResponse } from "../../types/api/moneyTransfer";
import Review from "./Review";
import { trackEvent } from "../../services/eventTracker";
import { EVENTS } from "../../constants/events";
import { FORM_NAMES } from "../../constants/forms";
import { ErrorObj } from "../../types/errors";

type CustomProps = {
  pageData: MoneyTransferPage;
  handleError: (error: unknown) => void;
}

const DynamicFormWrapper: React.FC<CustomProps & InjectedFormProps<{}, CustomProps>> = ({ pageData, handleError, handleSubmit, error, valid, submitting, submitFailed }) => {
  const dispatch = useDispatch();
  const workflow = useSelector((state: RootState) => state.moneyTransfer.workflow);
  const pageNumber = workflow.pages.findIndex((page: WorkflowStep) => page.type === pageData.id) + 1;

  useEffect(() => {
    window.scrollTo(0, 0);

    // Set up initial values for redux-form based on sections.fields that we get back from api
    // But don't do this if the form has been submitted and failed, otherwise we reinitialize the form
    if (pageData.id === FIELD_CATEGORIES.SENDER && !submitFailed) {
      const initialValues: Record<string, string | undefined> = {};
      pageData.sections.forEach(section => {
        section.fields.forEach(field => {
          initialValues[field.serviceProviderFieldId] = field.value
        })
      })

      dispatch(initialize(FORM_NAMES.WU_2_ACCOUNT, initialValues));
    }

  }, []);

  const onNext = () => {
    handleGAEvent();
    dispatch(SetWorkflowPageNumber(pageNumber + 1));
  }

  const onBack = () => {
    dispatch(SetWorkflowPageNumber(pageNumber - 1));
  }

  /**
   * Handle sending events to GA
   */
  const handleGAEvent = () => {
    let eventName: string | undefined;
    switch (pageData.id) {
      case FIELD_CATEGORIES.SENDER:
        eventName = EVENTS.TO_ACCOUNT.SENDER_INFO_COMPLETED;
        break;
      case FIELD_CATEGORIES.RECEIVER:
        eventName = EVENTS.TO_ACCOUNT.RECEIVER_INFO_COMPLETED;
        break;
      case FIELD_CATEGORIES.BANK:
        eventName = EVENTS.TO_ACCOUNT.BANK_INFO_COMPLETED;
        break;
    }
    if (eventName) trackEvent(eventName);
  };

  const renderFormErrors = (errors: ErrorObj[] | undefined | string) => {
    if (Array.isArray(errors) && errors.length > 0) {
      return (
        <ul className="bullet-list error-list">
          {errors.map((error) => (
            <li key={error.errorCode}>{error.errorMessage}</li>
          ))}
        </ul>
      );
    } else if (typeof errors === "string" && errors.length > 0) {
      return (
        <ul className="bullet-list error-list">
          <li>{errors}</li>
        </ul>
      );
    }
  }

  /**
   * Receiver (bank account) fields use dot notation in field name
   * Redux is saving that field name as a nested object in the form values.
   * E.g. { receiver: bank_account: { account_number: 1234 }}
   * When we get an error the field will be receiver.bank_account.account_number, but we want to update the field with an error in redux
   * So we need to convert the dot notation string to an object to update the field with an error.
   */
  const formatDotNotationStringToObj = (key: string, value: string) => {
    const result: Record<string, any> = {};
    let object: Record<string, any> = result;
    const arr = key.split('.');
    for (let i = 0; i < arr.length - 1; i++) {
      object = object[arr[i]] = {};
    }
    object[arr[arr.length - 1]] = value;
    return result;
  };

  /**
   * Receiver bank account fields come to us in a nested object.
   * This function flattens the object to a single level to send to the api.
   * E.g. { receiver: bank_account: { account_number: 1234 }} becomes { receiver.bank_account.account_number: 1234 }
   */
  const flattenObject = (ob: Record<string, any>) => {
    const toReturn: Record<string, any> = {};
    for (const i in ob) {
      if (i === "senderPhone") {
        toReturn[i] = ob[i];
        continue;
      }
      if (!ob.hasOwnProperty(i)) continue;
      if (typeof ob[i] === 'object' && ob[i] !== null) {
        const flatObject = flattenObject(ob[i]);
        for (const x in flatObject) {
          if (!flatObject.hasOwnProperty(x)) continue;
          toReturn[i + '.' + x] = flatObject[x];
        }
      } else {
        toReturn[i] = ob[i];
      }
    }
    return toReturn;
  };

  /**
   * Takes the values from the form and flattens them to a single level to send to the api.
   */
  const workflowValues = (values: Record<string, any> | undefined) => {
    const flattenedValues = flattenObject(values ?? {});
    let fields: { serviceProviderFieldId: string; value: any }[] = [];
    if (values != undefined) {
      fields = Object.entries(flattenedValues).map(
        ([serviceProviderFieldId, value]) => ({ serviceProviderFieldId, value })
      );
    }
    return fields;
  };

  const createError = (field: MoneyTransferField) => {
    // if field name has dot notation, we need to format it to an object
    if (field.serviceProviderFieldId.includes('.')) {
      const obj = formatDotNotationStringToObj(field.serviceProviderFieldId, field.validationError[0].errorMessage);
      return obj;
    } else {
      return { [field.serviceProviderFieldId]: field.validationError[0].errorMessage }
    }
  }

  /**
   * Gets errors from the workflow object based on the category passed in.
   * This is used to trigger validation on the sender/receiver pages.
   * */
  const handleWorkflowErrors = (pages: MoneyTransferPage[]) => {
    let errors = {};
    let pageIdWithError: string | null = null;
    if (pages) {

      pages.forEach((page: MoneyTransferPage) => {
        if (page.sections) {
          for (let section of page.sections) {
            for (let field of section.fields) {
              if (field.validationError?.length > 0) {
                const error = createError(field);
                errors = { ...errors, ...error };
                if (!pageIdWithError) {
                  pageIdWithError = page.id;
                }
              }
            }
          }
        }
      });
    }

    return {
      errors,
      pageIdWithError
    }
  }

  /**
   * Finds bank name field from response, if exists, and sends bank name to GA
   * */
  const handleBankNameGAEvent = (response: ValidateWorkflowResponse) => {
    // Find bank name field from response and send bank name to GA
    const bankNameField = response.result.displaySections.find(section => section.id === FIELD_CATEGORIES.BANK)?.fields.find(field => field.id === MONEY_TRANSFER_FIELD_IDS.BANK_NAME);
    if (bankNameField) {
      trackEvent(EVENTS.TO_ACCOUNT.BANK_INFO_COMPLETED, {
        [EVENTS.PARAMS.WU_TO_ACCOUNT_BANK_NAME]: bankNameField.formattedValue
      })
    }
  }

  const onSubmit = async (e: Record<string, any>) => {
    // If we are on the last page, we need to validate the workflow
    // Else, we can just move to the next page
    if (pageNumber === workflow.pages.length) {
      const values = workflowValues(e);

      try {
        const response = await MoneyTransferApi.ValidateWorkflow(workflow.quoteId, values, workflow.serviceType) as ValidateWorkflowResponse;

        handleBankNameGAEvent(response);

        dispatch(AddWorkflowStep(
          {
            number: workflow.pages.length + 1,
            order: workflow.pages.length + 1,
            title: response.result.title,
            type: MONEY_TRANSFER_STEPS.REVIEW,
            renderComponent: () => (
              <Review
                pageData={response}
                handleError={handleError}
              />
            )
          }
        ))

      } catch (err) {
        // If the error is a validation error, we need to handle the errors and display them
        const error = err as { status?: number; data?: { result?: { pages?: MoneyTransferPage[] } } };
        if (error.status === HTTP_STATUS_CODES.VALIDATION_ERROR) {
          const { errors, pageIdWithError } = handleWorkflowErrors(error.data?.result?.pages ?? []);
          if (errors && Object.keys(errors).length > 0) {
            const page = workflow.pages.find((page: WorkflowStep) => page.type === pageIdWithError);
            if (page) dispatch(SetWorkflowPageNumber(page.order));
            throw new SubmissionError(errors);
          } else {
            handleError(err);
          }
        } else {
          handleError(err);
        }
      }
    } else {
      onNext();
    }
  }

  return (
    <>
      {pageData &&
        <p className="large spacing-bottom-small">
          {pageData.subTitle}
        </p>
      }


      {pageData &&
        <DisplaySection sections={pageData.displaySections} />
      }

      <form onSubmit={handleSubmit(onSubmit)}>
        {renderFormErrors(error)}

        {pageData && (
          <DynamicForm fieldCategory={pageData} formName={FORM_NAMES.WU_2_ACCOUNT} />
        )}

        <div className="flex-row justify-content-start mb-16">
          <Action
            type="submit"
            loading={submitting}
            disabled={!valid}
            title={pageData.nextButton.title}
            displayType={ACTION_DISPLAY_TYPES.PRIMARY}
          />
        </div>

        {workflow.footer &&
          <FooterLinks
            disclaimerLinks={workflow.footer.disclaimerLinks}
            pageName={pageData.id}
          />
        }

      </form>
    </>
  );
}

const form = reduxForm<{}, CustomProps>({
  form: FORM_NAMES.WU_2_ACCOUNT,
  destroyOnUnmount: false,
})(DynamicFormWrapper);

export default form;